import { Translate } from '@change-corgi/core/react/i18n';
import { SocialButton } from '@change-corgi/design-system-legacy/components/social';
import { Box } from '@change-corgi/design-system-legacy/layout';
import { useIsSunriseTheme } from '@change-corgi/design-system-legacy/theme';
import { Heading } from '@change-corgi/design-system-legacy/typography';

import { ResponsiveBox } from 'src/app/pages/petitionGamma/details/shared/components';
import { AfterMediaWaypoint } from 'src/app/pages/petitionGamma/details/shared/waypoints';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';

import { usePetitionDetails } from '../../hooks/petitionDetails/petitionDetails';
import { usePhoto } from '../../hooks/photo';
import { useFirstMovement } from '../../hooks/useFirstMovement';
import { MediaContainer } from '../Media';
import { MovementContainer } from '../Movement';

import { usePetitionHeadingMobile } from './hooks/usePetitionHeadingMobile';

export function HeadingContainerMobile(): JSX.Element {
	const details = usePetitionDetails();
	const photo = usePhoto();
	const firstMovement = useFirstMovement();
	const isMobile = useIsMobile();
	const { actions } = usePetitionHeadingMobile();
	const isSunriseThemeEnabled = useIsSunriseTheme();

	return (
		<Box data-testid="heading-container-mobile" sx={{ position: 'relative' }}>
			{isMobile && photo && (
				<Box
					sx={{
						position: 'absolute',
						top: '20px',
						right: '16px',
						zIndex: 1,
						borderWidth: '0px !important',
					}}
				>
					<SocialButton
						channel="mobileNativeShare"
						iconOnly
						size="large"
						onClick={actions.handleShare}
						data-qa="native-share-button"
						data-testid="native-share-button"
						sx={{
							// This is doing a ton of overrides of DSLegacy's styles, which is hacky and error-prone,
							// especially because switching theme can affect it. There should just be a "circular icon
							// button" in the DS, but no point adding that ot the legacy DS.
							width: '50px',
							height: '50px',
							minWidth: '50px !important',
							minHeight: '50px !important',
							borderRadius: '50% !important',
							borderWidth: '0px',
							// eslint-disable-next-line @typescript-eslint/naming-convention
							'&:before': {
								borderWidth: '0px',
							},
							boxShadow: '0px 2px 12px 0px rgba(58, 41, 62, 0.25)',
							padding: '12px !important',
						}}
					>
						<Translate value="corgi.petition_gamma.share.mobile_native_share" />
					</SocialButton>
				</Box>
			)}

			<Box px={0} pt={[0, 8]} pb={[null, 8]}>
				{!!photo && <MediaContainer key={details.id} photo={photo} />}
				<AfterMediaWaypoint />
			</Box>

			<ResponsiveBox viewportSize="small">
				<Box
					pt={[16, 40]}
					px={[16, 0]}
					mt={[photo ? -45 : 0, 0]}
					mb={[16, 0]}
					mx={16}
					sx={{
						position: 'relative',
						background: 'white',
						borderRadius: '10px',
					}}
				>
					<Heading
						as="h1"
						pt={0}
						breakWord
						data-qa="petition-title"
						sx={{
							// The legacy attempt to have consistent font sizes would have us use `size="h2"` as a prop,
							// which would set both the fontSize and lineHeight.
							//
							// The lineHeight was initially overridden to 44px match Gamma's original redesign.
							//
							// Now we are trying to match Sunrise, which has a new set of sizes and font family.
							//
							// The fontSize happened to remain 32px for this heading (on small viewports) during the
							// switch from Gamma to Sunrise. The legacy "h2" was 32px, but this was coincidence rather
							// than semantics. In Sunrise semantics, this is `--font-size-4xl`, `--font-line-height-xl`
							// (Mobile mode).
							fontSize: '32px',
							lineHeight: isSunriseThemeEnabled ? '40px' : '44px',
							// Unrelated to the font notes above.
							textAlign: 'center',
						}}
					>
						{details.ask}
					</Heading>
				</Box>
			</ResponsiveBox>

			{firstMovement && (
				<Box mb={[16, 8]} px={[16, 0]}>
					<MovementContainer movement={firstMovement} alwaysExpanded={!photo} />
				</Box>
			)}
		</Box>
	);
}
